import type { Uuid } from '@common/types';
import type {
    DeviceMode,
    DrawerState,
    EDrawerType,
    ESelectedEntityType,
    PanelPosition,
    PreviewMode,
    SelectedEntity,
} from '@common/types/layout.type';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type Observable } from 'rxjs';

import LayoutActions from './layout.actions';
import * as LayoutSelectors from './layout.selectors';

@Injectable()
export class LayoutFacade {
    private readonly store = inject(Store);

    public readonly selectedEntity$: Observable<SelectedEntity> = this.store.select(
        LayoutSelectors.selectSelectedEntity
    );
    public readonly entityType$: Observable<ESelectedEntityType> = this.store.select(LayoutSelectors.selectEntityType);
    public readonly entityId$: Observable<Uuid> = this.store.select(LayoutSelectors.selectEntityId);
    public readonly showSettings$: Observable<boolean> = this.store.select(LayoutSelectors.selectShowSettings);

    public readonly previewMode$: Observable<PreviewMode> = this.store.select(LayoutSelectors.selectPreviewMode);
    public readonly isPreview$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsPreview);
    public readonly isPreviewMobile$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsPreviewMobile);
    public readonly isPreviewDesktop$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsPreviewDesktop);
    public readonly isPreviewTablet$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsPreviewTablet);
    public readonly deviceMode$: Observable<DeviceMode> = this.store.select(LayoutSelectors.selectDeviceMode);
    public readonly isDeviceMobile$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsDeviceMobile);
    public readonly isDeviceTablet$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsDeviceTablet);
    public readonly isDeviceDesktop$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsDeviceDesktop);
    public readonly showBlockDrawer$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsBlockDrawer);
    public readonly showPageDrawer$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsPageDrawer);
    public readonly showNavigationDrawer$: Observable<boolean> = this.store.select(
        LayoutSelectors.selectIsNavigationDrawer
    );
    public readonly showAiChatDrawer$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsAiChatDrawer);
    public readonly blockIndex$: Observable<number> = this.store.select(LayoutSelectors.selectBlockIndex);
    public readonly drawerStatType$: Observable<EDrawerType> = this.store.select(LayoutSelectors.selectDrawerStateType);

    public readonly isEntityDragging$: Observable<boolean> = this.store.select(LayoutSelectors.selectIsEntityDragging);
    public readonly isColumnWidthChanging$: Observable<boolean> = this.store.select(
        LayoutSelectors.selectIsColumnWidthChanging
    );
    public readonly hideControls$: Observable<boolean> = this.store.select(LayoutSelectors.selectHideControls);
    public readonly isCanvasPreviewActive$: Observable<boolean> = this.store.select(
        LayoutSelectors.selectIsCanvasPreviewActive
    );
    public readonly hideColumnControls$: Observable<boolean> = this.store.select(
        LayoutSelectors.selectHideColumnControls
    );
    public readonly settingsPanelPosition$: Observable<PanelPosition> = this.store.select(
        LayoutSelectors.selectSettingsPanelPosition
    );

    public setSelectedEntity(selectedEntity: SelectedEntity, scrollTo: boolean = false): void {
        this.store.dispatch(LayoutActions.setSelectedEntity({ selectedEntity, scrollTo }));
    }

    public setSelectedEntityWithScroll(selectedEntity: SelectedEntity): void {
        this.store.dispatch(LayoutActions.setSelectedEntity({ selectedEntity, scrollTo: true }));
    }

    public unsetEntity(): void {
        this.store.dispatch(LayoutActions.unsetEntity());
    }

    public hideSelectedEntitySettings(): void {
        this.store.dispatch(LayoutActions.hideSelectedEntitySettings());
    }

    public showPreview(): void {
        this.store.dispatch(LayoutActions.showPreview());
    }

    public changePreviewMode(previewMode: PreviewMode): void {
        this.store.dispatch(LayoutActions.changePreviewMode({ previewMode }));
    }

    public changeDeviceMode(deviceMode: DeviceMode): void {
        this.store.dispatch(LayoutActions.changeDeviceMode({ deviceMode }));
    }

    public hidePreview(): void {
        this.store.dispatch(LayoutActions.hidePreview());
    }

    public openDrawer(drawerState: DrawerState): void {
        this.store.dispatch(LayoutActions.openDrawer({ drawerState }));
    }

    public closeDrawer(): void {
        this.store.dispatch(LayoutActions.closeDrawer());
    }

    public setIsEntityDragging(isEntityDragging: boolean): void {
        this.store.dispatch(LayoutActions.setIsEntityDragging({ isEntityDragging }));
    }

    public setColumnWidthChanging(isColumnWidthChanging: boolean): void {
        this.store.dispatch(LayoutActions.setColumnWidthChanging({ isColumnWidthChanging }));
    }

    public togglePageDrawerAction(): void {
        this.store.dispatch(LayoutActions.togglePageDrawer());
    }

    public setIsHideColumnControls(isHideColumnControls: boolean): void {
        this.store.dispatch(LayoutActions.setIsHideColumnControls({ isHideColumnControls }));
    }

    public setSettingsPanelPosition(x: number, y: number): void {
        this.store.dispatch(LayoutActions.setSettingsPanelPosition({ x, y }));
    }

    public setAiReplyActive(active: boolean): void {
        this.store.dispatch(LayoutActions.setAiReplyActive({ active }));
    }

    public setElementPickerActive(active: boolean): void {
        this.store.dispatch(LayoutActions.setElementPickerActive({ active }));
    }
}
